import type {
  UserAccount as GQLItem,
  UserAccountConnection as GQLConnection,
} from '../../../generated/api.graphql';
import { take } from '../../../util';
import * as GqlConnection from '../../connection/GqlConnection';
import * as PageInfo from '../../connection/PageInfo';
import * as TeamRole from '../TeamRole/TeamRole';
import * as User from '../User/User';

/**
 * A user's membership in an account — the Team screen's row.
 *
 * Deliberately faithful to the schema: no derived `teamRoleId`. The Team form binds one, but
 * that is a form concern and belongs where the form is built.
 */
export type UserAccount = {
  id?: number;
  role?: string;
  teamRole?: TeamRole.TeamRole;
  user?: User.User;
};

export const fromGql = (item: GQLItem): UserAccount => ({
  ...take<UserAccount>(item, 'id', 'role'),
  teamRole: item.teamRole ? TeamRole.fromGql(item.teamRole) : undefined,
  user: item.user ? User.fromGql(item.user) : undefined,
});

/**
 * Mapped by hand rather than through `GqlConnection.fromGql`, for the reason `Internal/User`
 * documents: the query selects `edges` only — selecting `nodes` beside them would serialize
 * every member twice — so the shared helper would leave `nodes` permanently `[]` while
 * `GqlConnection<T>` promises a populated array. Deriving it from the edges keeps that promise.
 *
 * `edges` and `node` are both nullable on the schema; graphql-ruby does not emit those nulls
 * here, but filtering rather than casting keeps the mapper from throwing.
 */
export const fromGqlConnection = (connection: GQLConnection): GqlConnection.GqlConnection<UserAccount> => {
  const edges = (connection.edges ?? [])
    .filter(edge => Boolean(edge?.node))
    .map(edge => ({ cursor: edge!.cursor, node: fromGql(edge!.node as GQLItem) }));

  return {
    edges,
    nodes: edges.map(edge => edge.node),
    pageInfo: PageInfo.fromGql(connection.pageInfo),
  };
};
